import React, { useEffect, useRef, useState } from 'react';
import { getService } from '@Client/runner.hooks/useService';
import IFlowStepAssistantResponse from '@Shared.Angular/@types/core/contracts/queryModel/flows/flowStepAssistantResponse';
import { Guid } from '@Shared.Angular/@types/guid';
import { TextButton } from '@Shared.Angular/flowingly.components/buttons/TextButton';

enum LoadingMode {
  FetchingSaved = 'fetchingSaved',
  Generating = 'generating'
}

interface IAssistantState {
  contextKey: string;
  loadingMode: LoadingMode | null;
  errorMessage: string | null;
  response: IFlowStepAssistantResponse | null;
}

export type Props = {
  flowId?: Guid;
  stepId?: Guid;
  isStepWaitingForYou: boolean;
  isStepCompleted: boolean;
};

const defaultErrorMessage =
  'Step Assist could not generate a response right now. Please try again, or continue completing this workflow manually.';

const getContextKey = (
  flowId: Guid | undefined,
  stepId: Guid | undefined
): string => `${flowId ?? ''}:${stepId ?? ''}`;

const createState = (
  contextKey: string,
  loadingMode: LoadingMode | null = null
): IAssistantState => ({
  contextKey,
  loadingMode,
  errorMessage: null,
  response: null
});

export const FlowAiAssistant = ({
  flowId,
  stepId,
  isStepWaitingForYou,
  isStepCompleted
}: Props) => {
  const flowApiService = getService('flowApiService');
  const requestSequence = useRef(0);
  const contextKey = getContextKey(flowId, stepId);
  const currentContextKey = useRef(contextKey);
  currentContextKey.current = contextKey;

  const isActionableStep = !!flowId && !!stepId && isStepCompleted !== true;
  const canShowRecommendation =
    isActionableStep && isStepWaitingForYou === true;
  const [state, setState] = useState<IAssistantState>(() =>
    createState(contextKey, isActionableStep ? LoadingMode.FetchingSaved : null)
  );

  const visibleState =
    state.contextKey === contextKey
      ? state
      : createState(
          contextKey,
          isActionableStep ? LoadingMode.FetchingSaved : null
        );
  const isLoading = visibleState.loadingMode !== null;
  const canGenerate = isActionableStep && !isLoading;

  useEffect(() => {
    const requestId = ++requestSequence.current;
    setState(
      createState(
        contextKey,
        isActionableStep ? LoadingMode.FetchingSaved : null
      )
    );

    if (!isActionableStep || !flowId || !stepId) {
      return;
    }

    const isCurrentRequest = (): boolean =>
      requestId === requestSequence.current &&
      contextKey === currentContextKey.current;

    flowApiService
      .getFlowStepAssistant(flowId, stepId)
      .then((response) => {
        if (!isCurrentRequest()) {
          return;
        }

        setState({
          contextKey,
          loadingMode: null,
          errorMessage: null,
          response
        });
      })
      .catch(() => {
        if (isCurrentRequest()) {
          setState(createState(contextKey));
        }
      });

    return () => {
      requestSequence.current++;
    };
  }, [contextKey, flowId, isActionableStep, stepId]);

  const generate = (): void => {
    if (!canGenerate || !flowId || !stepId) {
      return;
    }

    const requestId = ++requestSequence.current;
    setState(createState(contextKey, LoadingMode.Generating));

    const isCurrentRequest = (): boolean =>
      requestId === requestSequence.current &&
      contextKey === currentContextKey.current;

    flowApiService
      .generateFlowStepAssistant(flowId, stepId)
      .then((response) => {
        if (!isCurrentRequest()) {
          return;
        }

        if (!response) {
          setState({
            contextKey,
            loadingMode: null,
            errorMessage: defaultErrorMessage,
            response: null
          });
          return;
        }

        setState({
          contextKey,
          loadingMode: null,
          errorMessage: null,
          response
        });
      })
      .catch(() => {
        if (isCurrentRequest()) {
          setState({
            contextKey,
            loadingMode: null,
            errorMessage: defaultErrorMessage,
            response: null
          });
        }
      });
  };

  return (
    <div className="flow-ai-assistant" aria-live="polite">
      {!visibleState.response && !isLoading && (
        <div className="flow-ai-assistant__state">
          <p className="flow-ai-assistant__empty-text">
            {isActionableStep ? (
              <span>
                {isStepWaitingForYou
                  ? 'Step Assist will summarize the workflow so far and suggest the next best action for the current step. If a clear decision is pending, it will recommend the option best supported by the workflow context.'
                  : 'Step Assist will summarize the workflow so far so you can understand what has happened in the workflow.'}
              </span>
            ) : (
              <span>Select an active step to use Step Assist.</span>
            )}
          </p>

          {visibleState.errorMessage && (
            <div className="flow-ai-assistant__error" role="alert">
              {visibleState.errorMessage}
            </div>
          )}

          {isActionableStep && (
            <TextButton
              type="primary"
              text="Generate"
              onClick={generate}
              disabled={!canGenerate}
              testId="flow-ai-assistant-generate-button"
              classNames="flow-ai-assistant__button"
            />
          )}
        </div>
      )}

      {isLoading && (
        <div className="flow-ai-assistant__loading" role="status">
          {visibleState.loadingMode === LoadingMode.Generating
            ? 'Generating AI response...'
            : 'Loading Step Assist...'}
        </div>
      )}

      {visibleState.response && !isLoading && (
        <div className="flow-ai-assistant__generated">
          <div className="flow-ai-assistant__section flow-ai-assistant__section--summary">
            <h5 className="flow-ai-assistant__heading">Summary</h5>
            <p className="flow-ai-assistant__body">
              {visibleState.response.summary}
            </p>
          </div>

          {canShowRecommendation && visibleState.response.recommendation && (
            <>
              <div className="flow-ai-assistant__section flow-ai-assistant__section--recommendation">
                <h5 className="flow-ai-assistant__heading">Next best action</h5>
                <p className="flow-ai-assistant__body">
                  {visibleState.response.recommendation}
                </p>
              </div>

              <p className="flow-ai-assistant__disclaimer">
                AI recommendations are advisory only. You remain responsible for
                the final workflow decision. Review comments separately before
                submitting.
              </p>
            </>
          )}
        </div>
      )}
    </div>
  );
};
